import { motion } from 'framer-motion';
import { useId } from 'react';
import { cn } from '../lib/cn';
import { springDefault, tapScale } from '../tokens';

type Option<T extends string> = {
  value: T;
  label: string;
};

type SegmentedControlProps<T extends string> = {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
};

/**
 * iOS-style segmented control. The selected thumb slides between segments
 * via a shared layoutId so the motion is automatic.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  const layoutId = useId();
  return (
    <div
      role="tablist"
      className={cn(
        'relative inline-flex p-1 rounded-button bg-surface-secondary w-full',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <motion.button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            whileTap={tapScale}
            transition={springDefault}
            className="relative flex-1 h-9 inline-flex items-center justify-center"
          >
            {active ? (
              <motion.span
                layoutId={layoutId}
                transition={springDefault}
                className="absolute inset-0 rounded-[10px] bg-surface shadow-sm"
              />
            ) : null}
            <span
              className={cn(
                'relative text-[13px] font-medium',
                active ? 'text-text-primary' : 'text-text-secondary',
              )}
            >
              {opt.label}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
